import { TimelineSegment, DueDateMarker } from '@/components/atoms';
import {
  TIMELINE_LAYOUT,
  SEGMENT_INFO,
  GAP_STYLE,
  GAP_OPPORTUNITY_COLORS,
  DEFAULT_COLOR_SCHEME,
} from '@/constants/timeline';
import type { AggregateTimelineData, ColorScheme, GroupTimeline, SegmentType, TooltipSegment } from '@/types/timeline';

export interface TimelineGroupHeaderProps {
  /** Group name (used for tooltip context) */
  groupName: string;
  /** Number of items in this group (used for tooltip context) */
  itemCount: number;
  /** Y position of this header */
  y: number;
  /** Total SVG width (chart area only, not including labels) */
  totalWidth: number;
  /** Group color (team color or gray for features) */
  color: string;
  /** Group timeline with blocks and gaps (shown when collapsed) */
  aggregateTimeline: GroupTimeline;
  /** Active color scheme for timeline segments */
  activeColors: ColorScheme;
  /** Color scheme ID for gap opportunity colors */
  colorSchemeId?: string;
  /** Convert date to X position */
  dateToX: (date: Date | null) => number;
  /** Callback when segment is hovered */
  onSegmentHover?: (segment: TooltipSegment | null, element: SVGElement | null) => void;
}

/** Render a simple solid bar for an interior work block (P50 start → P50 finish) */
function renderSimpleBlock(
  block: AggregateTimelineData,
  barY: number,
  activeColors: ColorScheme,
  dateToX: (date: Date | null) => number,
  createHoverHandler: (type: SegmentType, start: Date, end: Date) => (e: React.MouseEvent<SVGElement>) => void,
  handleMouseLeave: () => void,
): React.ReactNode {
  if (!block.startP50 || !block.finishP50) return null;
  return (
    <TimelineSegment
      x={dateToX(block.startP50)}
      y={barY}
      width={dateToX(block.finishP50) - dateToX(block.startP50)}
      height={TIMELINE_LAYOUT.BAR_HEIGHT}
      fill={activeColors.activeWork}
      segmentType="activeWork"
      isFirst={true}
      isLast={true}
      radius={TIMELINE_LAYOUT.BAR_RADIUS}
      onMouseEnter={(e) => createHoverHandler('activeWork', block.startP50!, block.finishP50!)(e)}
      onMouseLeave={handleMouseLeave}
    />
  );
}

/** Render full probability segments for the final work block */
function renderFinalBlock(
  block: AggregateTimelineData,
  barY: number,
  activeColors: ColorScheme,
  dateToX: (date: Date | null) => number,
  createHoverHandler: (type: SegmentType, start: Date, end: Date) => (e: React.MouseEvent<SVGElement>) => void,
  handleMouseLeave: () => void,
): React.ReactNode {
  return (
    <>
      {/* Active work: P50 start - P50 finish */}
      {block.startP50 && block.finishP50 && (
        <TimelineSegment
          x={dateToX(block.startP50)}
          y={barY}
          width={dateToX(block.finishP50) - dateToX(block.startP50)}
          height={TIMELINE_LAYOUT.BAR_HEIGHT}
          fill={activeColors.activeWork}
          segmentType="activeWork"
          isFirst={true}
          radius={TIMELINE_LAYOUT.BAR_RADIUS}
          onMouseEnter={(e) => createHoverHandler('activeWork', block.startP50!, block.finishP50!)(e)}
          onMouseLeave={handleMouseLeave}
        />
      )}

      {/* Maybe completion: P50 - P70 */}
      {block.finishP50 && block.finishP70 && (
        <TimelineSegment
          x={dateToX(block.finishP50)}
          y={barY}
          width={dateToX(block.finishP70) - dateToX(block.finishP50)}
          height={TIMELINE_LAYOUT.BAR_HEIGHT}
          fill={activeColors.maybeCompletion}
          segmentType="maybeCompletion"
          onMouseEnter={(e) => createHoverHandler('maybeCompletion', block.finishP50!, block.finishP70!)(e)}
          onMouseLeave={handleMouseLeave}
        />
      )}

      {/* Probable completion: P70 - P85 */}
      {block.finishP70 && block.finishP85 && (
        <TimelineSegment
          x={dateToX(block.finishP70)}
          y={barY}
          width={dateToX(block.finishP85) - dateToX(block.finishP70)}
          height={TIMELINE_LAYOUT.BAR_HEIGHT}
          fill={activeColors.probableCompletion}
          segmentType="probableCompletion"
          onMouseEnter={(e) => createHoverHandler('probableCompletion', block.finishP70!, block.finishP85!)(e)}
          onMouseLeave={handleMouseLeave}
        />
      )}

      {/* High probable: P85 - P95 */}
      {block.finishP85 && block.finishP95 && (
        <TimelineSegment
          x={dateToX(block.finishP85)}
          y={barY}
          width={dateToX(block.finishP95) - dateToX(block.finishP85)}
          height={TIMELINE_LAYOUT.BAR_HEIGHT}
          fill={activeColors.highProbable}
          segmentType="highProbable"
          isLast={true}
          radius={TIMELINE_LAYOUT.BAR_RADIUS}
          onMouseEnter={(e) => createHoverHandler('highProbable', block.finishP85!, block.finishP95!)(e)}
          onMouseLeave={handleMouseLeave}
        />
      )}
    </>
  );
}

/**
 * Timeline group header molecule - renders aggregate timeline bars for a group
 *
 * This component renders only the timeline bars for collapsed groups.
 * Labels and expand/collapse controls are handled by TimelineLabelColumn.
 *
 * Note: Bars start at x=0 since this component renders in the chart area only.
 */
export function TimelineGroupHeader({
  groupName,
  itemCount,
  y,
  totalWidth,
  color,
  aggregateTimeline,
  activeColors,
  colorSchemeId = DEFAULT_COLOR_SCHEME,
  dateToX,
  onSegmentHover,
}: TimelineGroupHeaderProps) {
  const rowHeight = TIMELINE_LAYOUT.ROW_HEIGHT;
  const barY = y + (rowHeight - TIMELINE_LAYOUT.BAR_HEIGHT) / 2;
  const countLabel = `${itemCount} deliverable${itemCount !== 1 ? 's' : ''}`;

  // Create hover handler factory for aggregate segments
  const createHoverHandler = (segmentType: SegmentType, startDate: Date, endDate: Date) => {
    return (event: React.MouseEvent<SVGElement>) => {
      onSegmentHover?.(
        {
          type: segmentType,
          label: SEGMENT_INFO[segmentType].label,
          probability: SEGMENT_INFO[segmentType].probability,
          startDate,
          endDate,
          deliverableName: `${groupName} (${countLabel})`,
          teamName: groupName,
          featureName: null,
        },
        event.currentTarget,
      );
    };
  };

  const handleMouseLeave = () => {
    onSegmentHover?.(null, null);
  };

  // Unique IDs for this component instance — strip characters that break SVG url(#id) references
  // (parentheses close the CSS url() function prematurely, > is XML-special)
  const sanitizedGroupName = groupName.replace(/[^a-zA-Z0-9-_]/g, '-');

  // Get opportunity colors for the current color scheme (with guaranteed fallback)
  const defaultOpportunityColors = { fill: '#FFF7ED', border: '#F59E0B' };
  const schemeColors = GAP_OPPORTUNITY_COLORS[colorSchemeId];
  const fallbackColors = GAP_OPPORTUNITY_COLORS[DEFAULT_COLOR_SCHEME];
  const opportunityColors: { fill: string; border: string } =
    schemeColors ?? fallbackColors ?? defaultOpportunityColors;

  return (
    <g>
      {/* SVG definitions for gap styles */}
      <defs>
        {/* Gradient for highlight style - soft golden center */}
        {GAP_STYLE === 'highlight' &&
          aggregateTimeline?.gaps.map((_, gapIndex) => (
            <linearGradient
              key={`highlight-${gapIndex}`}
              id={`gap-highlight-${sanitizedGroupName}-${gapIndex}`}
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor={opportunityColors.border} stopOpacity="0.2" />
              <stop offset="50%" stopColor={opportunityColors.border} stopOpacity="0.5" />
              <stop offset="100%" stopColor={opportunityColors.border} stopOpacity="0.2" />
            </linearGradient>
          ))}
        {/* Gradient for magnetic style - uses border color throughout for better visibility */}
        {GAP_STYLE === 'magnetic' &&
          aggregateTimeline?.gaps.map((_, gapIndex) => (
            <linearGradient
              key={`magnetic-${gapIndex}`}
              id={`gap-magnetic-${sanitizedGroupName}-${gapIndex}`}
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor={opportunityColors.border} stopOpacity="0.7" />
              <stop offset="20%" stopColor={opportunityColors.border} stopOpacity="0.4" />
              <stop offset="80%" stopColor={opportunityColors.border} stopOpacity="0.4" />
              <stop offset="100%" stopColor={opportunityColors.border} stopOpacity="0.7" />
            </linearGradient>
          ))}
      </defs>

      {/* Row background */}
      <rect x={0} y={y} width={totalWidth} height={rowHeight} fill={color} opacity={0.15} />

      {/* Aggregate timeline blocks with gap connectors */}
      {aggregateTimeline && (
        <>
          {/* Render gap connectors between blocks - opportunity-focused styles */}
          {aggregateTimeline.gaps.map((gap, gapIndex) => {
            const gapStartX = dateToX(gap.start);
            const gapEndX = dateToX(gap.end);
            const gapWidth = gapEndX - gapStartX;
            const gapHeight = TIMELINE_LAYOUT.BAR_HEIGHT / 3;
            const gapY = barY + (TIMELINE_LAYOUT.BAR_HEIGHT - gapHeight) / 2;

            if (GAP_STYLE === 'highlight') {
              // Opportunity Highlight: soft golden glow with dashed border
              return (
                <g key={`gap-${gapIndex}`}>
                  {/* Soft gradient background */}
                  <rect
                    x={gapStartX}
                    y={gapY}
                    width={gapWidth}
                    height={gapHeight}
                    fill={`url(#gap-highlight-${sanitizedGroupName}-${gapIndex})`}
                    rx={2}
                  />
                  {/* Dashed border to suggest "could be filled" */}
                  <rect
                    x={gapStartX}
                    y={gapY}
                    width={gapWidth}
                    height={gapHeight}
                    fill="none"
                    stroke={opportunityColors.border}
                    strokeWidth={1}
                    strokeDasharray="4 2"
                    strokeOpacity={0.5}
                    rx={2}
                  />
                </g>
              );
            } else {
              // Magnetic Pull: gradient pulling toward edges
              return (
                <g key={`gap-${gapIndex}`}>
                  {/* Gradient background with pull effect */}
                  <rect
                    x={gapStartX}
                    y={gapY}
                    width={gapWidth}
                    height={gapHeight}
                    fill={`url(#gap-magnetic-${sanitizedGroupName}-${gapIndex})`}
                    rx={2}
                  />
                  {/* Inner shadow effect on edges (simulated with small rects) */}
                  <rect
                    x={gapStartX}
                    y={gapY}
                    width={Math.min(4, gapWidth / 4)}
                    height={gapHeight}
                    fill={opportunityColors.border}
                    opacity={0.3}
                    rx={2}
                  />
                  <rect
                    x={gapEndX - Math.min(4, gapWidth / 4)}
                    y={gapY}
                    width={Math.min(4, gapWidth / 4)}
                    height={gapHeight}
                    fill={opportunityColors.border}
                    opacity={0.3}
                    rx={2}
                  />
                </g>
              );
            }
          })}

          {/* Render work blocks on top of gaps */}
          {aggregateTimeline.blocks.map((block, blockIndex) => {
            const isLastBlock = blockIndex === aggregateTimeline.blocks.length - 1;
            const renderer = isLastBlock ? renderFinalBlock : renderSimpleBlock;
            return (
              <g key={`block-${blockIndex}`}>
                {renderer(block, barY, activeColors, dateToX, createHoverHandler, handleMouseLeave)}
              </g>
            );
          })}

          {/* Feature due date marker (separate from deliverable due dates) */}
          {aggregateTimeline.featureDueDate && (
            <DueDateMarker
              x={dateToX(aggregateTimeline.featureDueDate)}
              barY={barY}
              barHeight={TIMELINE_LAYOUT.BAR_HEIGHT}
              dueDate={aggregateTimeline.featureDueDate}
              probability={aggregateTimeline.featureDueDateProbability ?? null}
              activeColors={activeColors}
            />
          )}
        </>
      )}
    </g>
  );
}
